import React from 'react'
import PropTypes from 'prop-types'

import * as yup from 'yup'
import useForm from 'react-hook-form'
import { reverse } from 'named-urls'
import { RHFInput } from 'react-hook-form-input'
import { Link } from 'react-router-dom'

import {
  TextField,
  Paper,
  Box,
  Divider,
  Button,
  Collapse,
  makeStyles,
} from '@material-ui/core'

import helpers from 'helpers'
import InputCpf from 'components/InputCpf'
import InputCnpj from 'components/InputCnpj'
import InputPhone from 'components/InputPhone'
import schemas from '../../schemas'
import { routes } from 'routes'

import styles from './styles'

const useStyles = makeStyles(styles)

const ClientDataForm = ({ clientId, data, onSubmit }) => {
  const classes = useStyles()
  const { register, handleSubmit, setValue, errors, watch } = useForm({
    validationSchema: schemas.data.shape({
      email: yup
        .string()
        .email()
        .emailAvailable({ currentEmail: data.email }),
      cpf: yup
        .string()
        .cpf()
        .cpfAvailable({
          currentCpf: helpers.people.isPerson(data.kind) ? data.cpf : null,
          kindRef: yup.ref('kind'),
        })
        .when('kind', {
          is: helpers.people.isPerson,
          then: yup.string().required(),
        }),
      cnpj: yup
        .string()
        .cnpj()
        .when('kind', {
          is: helpers.people.isCompany,
          then: yup
            .string()
            .cnpjAvailable({
              currentCnpj: helpers.people.isCompany(data.kind)
                ? data.cnpj
                : null,
            })
            .required(),
        }),
    }),
    defaultValues: {
      ...data,
      ...(helpers.people.isPerson(data.kind)
        ? { companyName: '', tradeName: '', cnpj: '' }
        : {}),
      phone: helpers.people.parsePhoneApi(data.contact),
      cpf: data.cpf || '',
      code: data.code || '',
    },
  })
  const values = watch()

  const handleChangeKind = () => {
    setValue('cnpj', '')
    setValue('tradeName', '')
    setValue('companyName', '')
  }

  return (
    <Paper>
      <form onSubmit={handleSubmit(onSubmit)} id="customer-edit-form">
        <Box p={2}>
          <RHFInput
            as={
              <TextField
                label="Nome"
                margin="dense"
                error={!!errors.name}
                helperText={errors && errors.name && errors.name.message}
                fullWidth
              />
            }
            name="name"
            register={register}
            setValue={setValue}
          />
          <RHFInput
            as={
              <TextField
                label="Email"
                margin="dense"
                error={!!errors.email}
                helperText={errors && errors.email && errors.email.message}
                fullWidth
              />
            }
            name="email"
            register={register}
            setValue={setValue}
          />
          <RHFInput
            as={
              <InputCpf
                label="CPF"
                margin="dense"
                error={!!errors.cpf}
                helperText={errors && errors.cpf && errors.cpf.message}
                fullWidth
              />
            }
            name="cpf"
            register={register}
            setValue={setValue}
          />
          <Collapse in={helpers.people.isCompany(values.kind)}>
            <RHFInput
              as={
                <TextField
                  label="Razão social"
                  margin="dense"
                  error={!!errors.companyName}
                  helperText={
                    errors && errors.companyName && errors.companyName.message
                  }
                  fullWidth
                />
              }
              name="companyName"
              register={register}
              setValue={setValue}
            />
            <RHFInput
              as={
                <TextField
                  label="Nome fantasia"
                  margin="dense"
                  error={!!errors.tradeName}
                  helperText={
                    errors && errors.tradeName && errors.tradeName.message
                  }
                  fullWidth
                />
              }
              name="tradeName"
              register={register}
              setValue={setValue}
            />
            <RHFInput
              as={
                <InputCnpj
                  label="CNPJ"
                  margin="dense"
                  error={!!errors.cnpj}
                  helperText={errors && errors.cnpj && errors.cnpj.message}
                  fullWidth
                />
              }
              name="cnpj"
              register={register}
              setValue={setValue}
            />
          </Collapse>
          <RHFInput
            as={
              <TextField
                label="Tipo"
                margin="dense"
                select={true}
                SelectProps={{ native: true }}
                fullWidth
              >
                <option value="C">Pessoa Jurídica</option>
                <option value="P">Pessoa Física</option>
              </TextField>
            }
            name="kind"
            register={register}
            setValue={(...params) => {
              handleChangeKind()
              setValue(...params)
            }}
          />
          <RHFInput
            as={
              <InputPhone
                label="Telefone"
                margin="dense"
                inputProps={{
                  maxLength: 15,
                }}
                error={!!errors.phone}
                helperText={errors && errors.phone && errors.phone.message}
                fullWidth
              />
            }
            name="phone"
            register={register}
            setValue={setValue}
          />
          <RHFInput
            as={
              <TextField
                label="Código de identificação"
                margin="dense"
                error={!!errors.code}
                helperText={errors && errors.code && errors.code.message}
                fullWidth
              />
            }
            name="code"
            register={register}
            setValue={setValue}
          />
        </Box>
        <Divider />
        <Box p={2}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            className={classes.button}
          >
            Salvar
          </Button>
          <Button
            color="primary"
            variant="outlined"
            component={Link}
            to={reverse(routes.clients.show, { clientId })}
          >
            Cancelar
          </Button>
        </Box>
      </form>
    </Paper>
  )
}

ClientDataForm.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
}

export default ClientDataForm
